// src/theme/ThemeContext.js
import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const ThemeContext = createContext();

const buildTheme = (paletteOverrides) =>
  createTheme({
    palette: {
      mode: paletteOverrides.mode,
      primary: { main: paletteOverrides.primary },
      secondary: { main: paletteOverrides.secondary },
      background: {
        default: paletteOverrides.background,
        paper: paletteOverrides.paper,
        secondPaper: paletteOverrides.secondPaper,
        guideCard: paletteOverrides.guideCard ?? paletteOverrides.paper, // fallback
      },
      text: {
        primary: paletteOverrides.text,
      },
      accent: { main: paletteOverrides.accent },
      cardHeader: { main: paletteOverrides.cardHeader },
      calendar: {
        main: paletteOverrides.pool,
        secondary: paletteOverrides.sale,
        default: paletteOverrides.event
      }
    },
    typography: {
      fontFamily: "'Roboto', sans-serif",
      h1: { fontFamily: "'Paytone One', sans-serif" },
      h2: { fontFamily: "'Paytone One', sans-serif" },
      h3: { fontFamily: "'Paytone One', sans-serif" },
      h4: { fontFamily: "'Paytone One', sans-serif" },
      h5: { fontFamily: "'Paytone One', sans-serif" },
      h6: { fontFamily: "'Paytone One', sans-serif" },
      button: { fontFamily: "'Paytone One', sans-serif" },
    },
    components: {
      MuiPickersDay: {
        styleOverrides: {
          root: {
            color: "#fff", // or theme.text.primary via callback
          },
          today: {
            borderColor: "#888",
          },
          selected: {
            backgroundColor: "#1976d2 !important",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            input: {
              color: paletteOverrides.text,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            scrollBehavior: "smooth",
          },
          body: { height: "100%", margin: 0 },
          "#root": {
            height: "100%",
            display: "flex",
            flexDirection: "column",
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            textDecoration: "none",
            color: paletteOverrides.secondary,
            transition: "all 0.1s ease-in-out",
            "&:hover": {
              color: paletteOverrides.primary,
              borderRadius: 4,
            },
          },
        },
      },
    },
  });

const themePresets = {
  goat: buildTheme({
    mode: "dark",
    primary: "#894AC5",
    secondary: "#E29279",
    background: "#130F1B",
    paper: "#271A3A",
    secondPaper: "#1B1129",
    guideCard: "#1F172C",
    text: "#E6D3D6",
    cardHeader: "#EEB86D",
    accent: "#EEB86D",
    pool: "#452276",
    sale: "#6E0C0C",
    event: "#8A6109",
  }),
  stylisher: buildTheme({
    mode: "light",
    primary: "#ef8aa9",
    secondary: "#94a4ef",
    background: "#fcf8f9",
    paper: "#FFEEF4",
    secondPaper: "#F8DCE5",
    guideCard: "#E6E1F3",
    text: "#363636",
    cardHeader: "#F69473",
    accent: "#efa78f",
    pool: "#D6EBFF",
    sale: "#FFDBD6",
    event: "#FAC6FF",
  }),
  dark: buildTheme({
    mode: "dark",
    primary: "#94b1db",
    secondary: "#8da3c4",
    background: "#05090a",
    paper: "#2A3343",
    secondPaper: "#181F2A",
    guideCard: "#1F2A33",
    text: "#edf4f7",
    cardHeader: "#be6d71",
    accent: "#be6d71",
    pool: "#435D74",
    sale: "#4E1515",
    event: "#437461",
  }),
  light: buildTheme({
    mode: "light",
    primary: "#000000",        // MUI default blue
    secondary: "#9c27b0",      // MUI default purple
    background: "#ffffff",     // white background
    paper: "#F2F2F2",          // also white
    secondPaper: "#ebebeb",          // also white
    guideCard: "#f5f5f5",      // light gray (optional)
    text: "#000000",           // black text
    cardHeader: "#1976d2",     // reuse primary as cardHeader
    accent: "#1976d2",         // reuse primary as accent
    pool: "#A6E6F0",
    sale: "#E57072",
    event: "#86E095",
  }),
};

export const CustomThemeProvider = ({ children }) => {
  const [themeName, setThemeName] = useState(() => {
    const stored = localStorage.getItem("theme");
    return themePresets[stored] ? stored : "goat";
  });

  const theme = useMemo(() => themePresets[themeName], [themeName]);

  const toggleTheme = () => {
    const order = ["goat", "stylisher", "dark", "light"];
    const nextIndex = (order.indexOf(themeName) + 1) % order.length;
    const next = order[nextIndex];
    setThemeName(next);
  };

  const setTheme = (name) => {
    if (themePresets[name]) {
      setThemeName(name);
    }
  };

  useEffect(() => {
    localStorage.setItem("theme", themeName);
  }, [themeName]);

  return (
    <ThemeContext.Provider value={{ themeName, toggleTheme, setTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);
